import { Smartphone, Globe, Server, Compass } from "lucide-react";

const services = [
    {
        id: "mobile",
        title: "Mobile App Development",
        description:
            "Production-grade native and cross-platform apps for Android and iOS, built for performance, offline reliability, and long-term maintainability.",
        icon: Smartphone,
        items: ["iOS & Android", "React Native", "Offline-first"],
    },
    {
        id: "web",
        title: "Web Development",
        description:
            "Fast, accessible, and SEO-friendly web platforms — from marketing sites to complex product dashboards with real-time data.",
        icon: Globe,
        items: ["React / Next.js", "TypeScript", "Design systems"],
    },
    {
        id: "backend",
        title: "Backend & APIs",
        description:
            "Robust APIs, event-driven services, and data pipelines engineered to scale reliably under real-world load.",
        icon: Server,
        items: ["REST & GraphQL", "Cloud-native", "Observability"],
    },
    {
        id: "consulting",
        title: "Consulting / System Design",
        description:
            "Architectural reviews, system design, and technical strategy to help your team ship confidently and scale without regret.",
        icon: Compass,
        items: ["System design", "Architecture reviews", "Team enablement"],
    },
];

export const Services = () => {
    return (
        <section
            id="services"
            data-testid="services-section"
            className="py-24 lg:py-32 bg-slate-50 border-y border-slate-200/60"
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-12">
                <div className="max-w-3xl">
                    <span className="text-xs sm:text-sm font-semibold uppercase tracking-[0.2em] text-blue-700 mb-4 block font-display">
                        What We Do
                    </span>
                    <h2
                        data-testid="services-title"
                        className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-slate-900 leading-[1.1]"
                    >
                        Services built for serious products.
                    </h2>
                    <p className="mt-6 text-base lg:text-lg font-normal leading-relaxed text-slate-600">
                        A focused set of capabilities, delivered with the
                        rigour your product deserves.
                    </p>
                </div>

                <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
                    {services.map((s) => {
                        const Icon = s.icon;
                        return (
                            <article
                                key={s.id}
                                data-testid={`service-card-${s.id}`}
                                className="group bg-white border border-slate-200 rounded-2xl p-8 lg:p-10 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:border-blue-200"
                            >
                                <div className="flex items-center justify-between">
                                    <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50 text-blue-700 group-hover:bg-blue-700 group-hover:text-white transition-colors duration-300">
                                        <Icon size={22} strokeWidth={1.8} />
                                    </div>
                                    <span className="text-xs font-medium uppercase tracking-[0.18em] text-slate-400">
                                        0{services.indexOf(s) + 1}
                                    </span>
                                </div>

                                <h3
                                    data-testid={`service-title-${s.id}`}
                                    className="mt-6 font-display text-xl sm:text-2xl font-medium tracking-tight text-slate-900"
                                >
                                    {s.title}
                                </h3>
                                <p className="mt-3 text-base leading-relaxed text-slate-600">
                                    {s.description}
                                </p>

                                <ul className="mt-6 flex flex-wrap gap-2">
                                    {s.items.map((i) => (
                                        <li
                                            key={i}
                                            className="text-xs font-medium text-slate-600 bg-slate-100 px-3 py-1.5 rounded-full"
                                        >
                                            {i}
                                        </li>
                                    ))}
                                </ul>
                            </article>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};
